import { QueryResult } from '@apollo/client/react';
import { Fragment, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import { get, groupBy } from 'lodash';
import moment from 'moment';

import { Switch } from './Switch';
import { Container } from './Container';
import { Skeleton } from './Skeleton';
import { Empty } from './Empty';
import { Infinity } from './Infinity';
import { Paragraph } from './Text';
import { Pill } from './Pill';

function itemsFromPagination<T>(array?: { edges: Array<{ node: T }> }) {
	if (!array) {
		return undefined;
	}

	return array.edges
		.slice()
		.reverse()
		.map(({ node }) => node as T);
}

function DynamicContainer(props: PropsWithChildren<{ compact?: boolean }>) {
	if (props.compact) {
		return <>{props.children}</>;
	}
	return <Container my={4}>{props.children}</Container>;
}

export function CardList<Q extends object, T extends { id: string }>(props: {
	translationName: string;
	query: QueryResult<Q>;
	toolbar?: ReactNode;
	path: string;
	archivedQuery?: QueryResult<Q>;
	fetchArchived?: () => void;
	groupedByYear?: boolean;
	skeletonRepeat?: number;
	disablePagination?: boolean;
	compact?: boolean;

	icon: ReactNode;
	renderSkeleton: () => ReactNode;
	renderCard: (item: T) => ReactNode;
}) {
	const { t } = useTranslation();

	const [showArchived, setShowArchived] = useState<boolean>(false);

	const rawItems = get(props.query.data, props.path);
	const items = props.disablePagination ? rawItems : itemsFromPagination<T>(rawItems);
	const archivedItems = props.archivedQuery?.data
		? itemsFromPagination<T>(get(props.archivedQuery.data, props.path))
		: null;

	const itemsByYear = items
		? groupBy(items, (item) => {
				return moment(item.createdAt).format('YYYY');
		  })
		: {};

	useEffect(() => {
		if (!showArchived) {
			return;
		}

		props.fetchArchived?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showArchived]);

	return (
		<DynamicContainer compact={props.compact}>
			{props.archivedQuery && (
				<Box mb={3}>
					<Switch
						label={t(`${props.translationName}.list.archived.button`)}
						on={showArchived}
						onChange={(on) => setShowArchived(on)}
					/>
				</Box>
			)}

			{props.toolbar}

			{!showArchived ? (
				<>
					{items ? (
						<>
							{items.length > 0 ? (
								<>
									{props.groupedByYear ? (
										<>
											{Object.keys(itemsByYear)
												.sort()
												.reverse()
												.map((year) => (
													<div id={year} key={year}>
														<Box mt={4} mb={1}>
															<a href={`#${year}`}>
																<Pill>{year}</Pill>
															</a>
														</Box>

														{itemsByYear[year].map((item: T) => (
															<Fragment key={item.id}>{props.renderCard(item)}</Fragment>
														))}
													</div>
												))}
											<Infinity<Q> query={props.query} path={props.path} reverse>
												<Skeleton.Repeat count={5}>{props.renderSkeleton()}</Skeleton.Repeat>
											</Infinity>
										</>
									) : (
										<>
											{items.map((item: T) => (
												<Fragment key={item.id}>{props.renderCard(item)}</Fragment>
											))}
											<Infinity<Q> query={props.query} path={props.path} reverse>
												<Skeleton.Repeat count={5}>{props.renderSkeleton()}</Skeleton.Repeat>
											</Infinity>
										</>
									)}
								</>
							) : (
								<Skeleton.Empty
									wide={props.compact}
									contents={
										<Empty
											icon={props.icon}
											title={t(`${props.translationName}.list.empty.title`)}
											description={t(`${props.translationName}.list.empty.description`)}
										/>
									}
								>
									<Skeleton.Repeat count={props.skeletonRepeat ?? props.compact ? 2 : 3}>
										{props.renderSkeleton()}
									</Skeleton.Repeat>
								</Skeleton.Empty>
							)}
						</>
					) : (
						<Skeleton.Repeat count={10}>{props.renderSkeleton()}</Skeleton.Repeat>
					)}
				</>
			) : (
				<>
					<Paragraph mb={3}>{t(`${props.translationName}.list.archived.title`)}</Paragraph>

					{archivedItems ? (
						<>
							{archivedItems.length > 0 ? (
								<>
									{archivedItems.map((node) => (
										<Fragment key={node.id}>{props.renderCard(node)}</Fragment>
									))}
									<Infinity<Q> query={props.archivedQuery!} path={props.path} reverse>
										<Skeleton.Repeat count={5}>{props.renderSkeleton()}</Skeleton.Repeat>
									</Infinity>
								</>
							) : (
								<Skeleton.Empty
									contents={
										<Empty
											icon={props.icon}
											title={t(`${props.translationName}.list.archived.empty.title`)}
											description={t(`${props.translationName}.list.archived.empty.description`)}
										/>
									}
								>
									<Skeleton.Repeat count={props.skeletonRepeat ?? props.compact ? 2 : 3}>
										{props.renderSkeleton()}
									</Skeleton.Repeat>
								</Skeleton.Empty>
							)}
						</>
					) : (
						<Skeleton.Repeat count={10}>{props.renderSkeleton()}</Skeleton.Repeat>
					)}
				</>
			)}
		</DynamicContainer>
	);
}
